import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  spacesListQueryOptions,
  personalSpaceQueryOptions,
  invalidateSpaceScopedQueries,
} from "./spaces-queries";
import { useSpaceStore } from "./space-store";

/**
 * Resolve the active space on app boot. Keeps a persisted id if it's still
 * accessible; otherwise lands the user in their personal space.
 *
 * Gate on BOTH queries — `spaces` usually wins the race, and falling back
 * to `spaces[0]` before `personal` arrives lands on a team space (memberships
 * are listed first), which the second pass can't undo because currentId is
 * by then valid.
 */
export function useSpaceBootstrap(isAuthenticated: boolean): void {
  const qc = useQueryClient();
  const currentId = useSpaceStore((s) => s.currentSpaceId);
  const setCurrent = useSpaceStore((s) => s.setCurrentSpaceId);
  const seedRecent = useSpaceStore((s) => s.seedRecentSpaceIds);

  const { data: spaces } = useQuery({
    ...spacesListQueryOptions(),
    enabled: isAuthenticated,
  });
  const { data: personal } = useQuery({
    ...personalSpaceQueryOptions(),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!spaces) return;
    // Seed MRU from server order on first run so the sidebar's recent
    // list has stable slots from day one (no jitter when the user first
    // clicks a space that was only visible via fallback).
    seedRecent(spaces.map((s) => s.id));
  }, [isAuthenticated, spaces, seedRecent]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!spaces || !personal) return;
    if (currentId && spaces.some((s) => s.id === currentId)) return;
    if (personal.id === currentId) return;
    setCurrent(personal.id);
    invalidateSpaceScopedQueries(qc);
  }, [isAuthenticated, spaces, personal, currentId, setCurrent, qc]);
}
